/*
 * File: ThemeContext.tsx
 * Project: rosita
 * -----
 * Copyright (c) 2023 Elegant Software Solutions All rights reserved.
 */

import React, { createContext, useContext, useEffect } from 'react';
import { useAppSelector } from 'redux/store';

// Create a context for theme
export const ThemeContext = createContext<{ isDarkMode: boolean }>({ isDarkMode: true });

// Custom hook to use the theme context
export const useTheme = () => useContext(ThemeContext);

// Theme provider component
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme } = useAppSelector((state) => state.app);
  const isDarkMode = theme === 'dark';

  // Apply theme to body element
  useEffect(() => {
    // Apply theme to body
    document.body.style.backgroundColor = isDarkMode ? '#141322' : '#f0f2f5';
    document.body.style.color = isDarkMode ? '#ffffff' : '#000000';

    // Apply theme to root element for CSS variables
    const root = document.documentElement;
    if (isDarkMode) {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.setAttribute('data-theme', 'light');
    }

    // Apply theme to all antd components
    const styleElement = document.createElement('style');
    styleElement.id = 'theme-override';

    if (isDarkMode) {
      styleElement.textContent = `
        .ant-table { background-color: #1f1e2e; color: #84859a; }
        .ant-table-thead > tr > th { background-color: #2a293b; color: #84859a; }
        .ant-table-tbody > tr > td { border-bottom: 1px solid #2a293b; }
        .ant-table-tbody > tr:hover > td { background-color: #2a293b; }
        .ant-pagination-item { background-color: #1f1e2e; border-color: #2a293b; }
        .ant-pagination-item a { color: #84859a; }
        .ant-pagination-item-active { border-color: #1890ff; }
        .ant-pagination-item-active a { color: #1890ff; }
        .ant-select-dropdown { background-color: #1f1e2e; }
        .ant-select-item { color: #84859a; }
        .ant-select-item-option-active:not(.ant-select-item-option-disabled) { background-color: #2a293b; }
        .ant-select-item-option-selected:not(.ant-select-item-option-disabled) { background-color: #1890ff; color: #fff; }
        .ant-modal-content, .ant-modal-header { background-color: #1f1e2e; color: #84859a; }
        .ant-modal-title { color: #84859a; }
        .ant-modal-close { color: #84859a; }
        .ant-form-item-label > label { color: #84859a; }
        .ant-input { background-color: #1f1e2e; border-color: #2a293b; color: #84859a; }
        .ant-input:hover, .ant-input:focus { border-color: #1890ff; }
        .ant-btn { border-color: #2a293b; }
        .ant-btn-primary { background-color: #1890ff; border-color: #1890ff; }
        .ant-card { background-color: #1f1e2e; border-color: #2a293b; }
        .ant-card-head { border-bottom-color: #2a293b; }
        .ant-card-head-title { color: #84859a; }
        .ant-menu.ant-menu-dark { background-color: #1f1e2e; }
        .ant-menu-dark .ant-menu-item-selected { background-color: #1890ff; }
      `;
    } else {
      styleElement.textContent = `
        .ant-table { background-color: #ffffff; color: rgba(0, 0, 0, 0.85); }
        .ant-table-thead > tr > th { background-color: #fafafa; color: rgba(0, 0, 0, 0.85); }
        .ant-table-tbody > tr > td { border-bottom: 1px solid #f0f0f0; }
        .ant-table-tbody > tr:hover > td { background-color: #f5f5f5; }
        .ant-pagination-item { background-color: #ffffff; border-color: #d9d9d9; }
        .ant-pagination-item a { color: rgba(0, 0, 0, 0.85); }
        .ant-pagination-item-active { border-color: #1890ff; }
        .ant-pagination-item-active a { color: #1890ff; }
        .ant-select-dropdown { background-color: #ffffff; }
        .ant-select-item { color: rgba(0, 0, 0, 0.85); }
        .ant-select-item-option-active:not(.ant-select-item-option-disabled) { background-color: #f5f5f5; }
        .ant-select-item-option-selected:not(.ant-select-item-option-disabled) { background-color: #e6f7ff; color: rgba(0, 0, 0, 0.85); }
        .ant-modal-content, .ant-modal-header { background-color: #ffffff; color: rgba(0, 0, 0, 0.85); }
        .ant-modal-title { color: rgba(0, 0, 0, 0.85); }
        .ant-modal-close { color: rgba(0, 0, 0, 0.45); }
        .ant-form-item-label > label { color: rgba(0, 0, 0, 0.85); }
        .ant-input { background-color: #ffffff; border-color: #d9d9d9; color: rgba(0, 0, 0, 0.85); }
        .ant-input:hover, .ant-input:focus { border-color: #40a9ff; }
        .ant-btn { border-color: #d9d9d9; }
        .ant-btn-primary { background-color: #1890ff; border-color: #1890ff; }
        .ant-card { background-color: #ffffff; border-color: #f0f0f0; }
        .ant-card-head { border-bottom-color: #f0f0f0; }
        .ant-card-head-title { color: rgba(0, 0, 0, 0.85); }
        .ant-menu.ant-menu-light { background-color: #ffffff; }
        .ant-menu-light .ant-menu-item-selected { background-color: #e6f7ff; color: #1890ff; }
      `;
    }

    // Remove existing style element if it exists
    const existingStyle = document.getElementById('theme-override');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Add the new style element
    document.head.appendChild(styleElement);

    // Clean up on unmount
    return () => {
      if (styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, [isDarkMode]);

  return <ThemeContext.Provider value={{ isDarkMode }}>{children}</ThemeContext.Provider>;
};
